import { Segmented, Select, Tooltip } from 'antd';
import {
  SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL_NOTE,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_LABELS,
  SUMMARY_ENTITY_OPERATION,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_PLAN_VS_ACTUAL_SECTION_ID,
} from 'constants/uiConstants';
import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IInput } from 'interfaces/IInput';
import React, { useMemo, useState } from 'react';
import { formatCurrency } from 'utils/CurrencyUtils';
import type {
  PlanVsActualCategoryFilter,
  PlanVsActualRow,
  SpendMetric,
} from './computePlanVsActual';
import { computePlanVsActual } from './computePlanVsActual';
import CopyButton from './CopyButton';
import { formatNumber } from './formatNumber';
import SectionCard from './SectionCard';
import SpendTimelineChart from './SpendTimelineChart';
import {
  buildTsv,
  groupInputsByCrop,
  resolveOldestCropGroup,
} from './summary2Utils';

/** Fixed order — matches Cost by Crop's category ordering (Production isn't a cost category, so it's excluded here too). */
const CATEGORY_FILTER_ORDER: PlanVsActualCategoryFilter[] = [
  SUMMARY_ENTITY_SEED,
  SUMMARY_ENTITY_SOIL_INPUT,
  SUMMARY_ENTITY_PLANT_PROTECTION,
  SUMMARY_ENTITY_BIOSTIMULANT,
  SUMMARY_ENTITY_OPERATION,
];

interface PlanVsActualChartProps {
  inputs: IInput[];
  gUnits: GUnit[];
  /** Shows the crop selector and the "Spend Over Time" burn-down sub-section — needs Paddock level or lower to reliably resolve to one crop. */
  showTimeline: boolean;
}

/** null when metric is 'perHa' and the row has no recorded area to divide by. */
const scaleValue = (
  cost: number,
  areaHa: number,
  metric: SpendMetric
): number | null => {
  if (metric === 'total') return cost;
  return areaHa > 0 ? cost / areaHa : null;
};

const variancePct = (row: PlanVsActualRow): number | null =>
  row.planCost > 0
    ? ((row.actualCost - row.planCost) / row.planCost) * 100
    : null;

const NoAreaCell: React.FC = () => (
  <Tooltip title="No area recorded for this crop — $/ha can't be calculated">
    <span className="cursor-help font-normal text-gray-muted">—</span>
  </Tooltip>
);

/** One shared 0→max scale across every crop, so bar lengths are comparable between rows (unlike the 100%-stacked bars in Cost by Crop). */
const CropPlanActualBars: React.FC<{
  row: PlanVsActualRow;
  max: number;
  metric: SpendMetric;
}> = ({ row, max, metric }) => {
  const planValue = scaleValue(row.planCost, row.areaHa, metric);
  const actualValue = scaleValue(row.actualCost, row.areaHa, metric);
  const planPct = max > 0 && planValue !== null ? (planValue / max) * 100 : 0;
  const actualPct =
    max > 0 && actualValue !== null ? (actualValue / max) * 100 : 0;

  const Bar = ({
    pct,
    value,
    label,
    fillClass,
  }: {
    pct: number;
    value: number | null;
    label: string;
    fillClass: string;
  }) => (
    <Tooltip
      title={
        value === null
          ? 'No area recorded for this crop — $/ha can’t be calculated'
          : `${label}: ${formatCurrency(value)}`
      }
    >
      <svg
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        className="h-3 w-full flex-1 cursor-help rounded-sm bg-gray-smoke"
      >
        {pct > 0 && (
          <rect
            x={0}
            y={0}
            width={pct}
            height={10}
            className={`${fillClass} transition-[filter] hover:brightness-90`}
          />
        )}
      </svg>
    </Tooltip>
  );

  return (
    <div className="flex flex-col gap-1">
      <span
        className="truncate text-sm font-semibold text-charcoal"
        title={row.cropLabel}
      >
        {row.cropLabel}
      </span>
      <div className="flex items-center gap-2">
        <span className="w-14 shrink-0 text-xs text-gray-muted">Plan</span>
        <Bar pct={planPct} value={planValue} label="Plan" fillClass="fill-chart-7" />
        <span className="w-20 shrink-0 text-right text-xs tabular-nums text-gray-dark">
          {planValue === null ? '—' : formatCurrency(planValue)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-14 shrink-0 text-xs text-gray-muted">Actual</span>
        <Bar pct={actualPct} value={actualValue} label="Actual" fillClass="fill-chart-8" />
        <span className="w-20 shrink-0 text-right text-xs tabular-nums text-gray-dark">
          {actualValue === null ? '—' : formatCurrency(actualValue)}
        </span>
      </div>
    </div>
  );
};

const VariancePctCell: React.FC<{ row: PlanVsActualRow }> = ({ row }) => {
  const pct = variancePct(row);
  if (pct === null) {
    return (
      <Tooltip title="No plan cost recorded for this crop — nothing to compare Actual spend against">
        <span className="cursor-help font-normal text-gray-muted">—</span>
      </Tooltip>
    );
  }
  const colorClass =
    pct > 0 ? 'text-danger' : pct < 0 ? 'text-success' : 'text-charcoal';
  return (
    <span className={colorClass}>
      {pct > 0 ? '+' : ''}
      {formatNumber(pct)}%
    </span>
  );
};

const PlanVsActualChart: React.FC<PlanVsActualChartProps> = ({
  inputs,
  gUnits,
  showTimeline,
}) => {
  const [categoryFilter, setCategoryFilter] =
    useState<PlanVsActualCategoryFilter>('all');
  const [metric, setMetric] = useState<SpendMetric>('total');

  // Crop selector — governs both the crop-rows table/bar chart above and
  // the Spend Over Time timeline below, so they always agree on what
  // they're showing. Only relevant once scoped to (largely) one crop
  // (Paddock level or lower); Org/Farm keeps comparing every crop as rows.
  const cropGroups = useMemo(() => groupInputsByCrop(inputs), [inputs]);
  const oldestGroup = useMemo(
    () => resolveOldestCropGroup(cropGroups),
    [cropGroups]
  );
  const [selectedCropKey, setSelectedCropKey] = useState<string | null>(null);
  const showCropSelector = showTimeline && cropGroups.length > 1;
  const activeGroup = showTimeline
    ? (cropGroups.find((g) => g.key === selectedCropKey) ?? oldestGroup)
    : undefined;
  const activeInputs = activeGroup ? activeGroup.inputs : inputs;

  // Existence check across every category — the section (and its filter
  // controls) stays visible as long as any category has data, even if the
  // currently-selected one doesn't.
  const hasAnyData = useMemo(
    () => computePlanVsActual(inputs, gUnits, 'all').length > 0,
    [inputs, gUnits]
  );

  const rows = useMemo(
    () => computePlanVsActual(activeInputs, gUnits, categoryFilter),
    [activeInputs, gUnits, categoryFilter]
  );

  // Footer totals sum each row's own displayed $ or $/ha value (matches
  // this codebase's established $/ha-footer convention — see
  // SummaryReportTable's costPerHaTotal) rather than re-deriving a blended
  // Σcost/Σarea, since rows can legitimately share the same paddock area.
  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          plan: acc.plan + (scaleValue(row.planCost, row.areaHa, metric) ?? 0),
          actual:
            acc.actual + (scaleValue(row.actualCost, row.areaHa, metric) ?? 0),
          planRaw: acc.planRaw + row.planCost,
          actualRaw: acc.actualRaw + row.actualCost,
        }),
        { plan: 0, actual: 0, planRaw: 0, actualRaw: 0 }
      ),
    [rows, metric]
  );
  const totalVariance = totals.actual - totals.plan;
  const totalVariancePct =
    totals.planRaw > 0
      ? ((totals.actualRaw - totals.planRaw) / totals.planRaw) * 100
      : null;

  const max = useMemo(
    () =>
      rows.reduce(
        (m, row) =>
          Math.max(
            m,
            scaleValue(row.planCost, row.areaHa, metric) ?? 0,
            scaleValue(row.actualCost, row.areaHa, metric) ?? 0
          ),
        0
      ),
    [rows, metric]
  );

  const categoryFilterLabel =
    categoryFilter === 'all' ? 'All Categories' : SUMMARY_ENTITY_LABELS[categoryFilter];
  const emptyMessage = [
    `No ${categoryFilterLabel} Plan or Actual data recorded`,
    activeGroup ? `for ${activeGroup.label}` : 'for this scope',
  ].join(' ');

  const unitSuffix = metric === 'total' ? '($)' : '($/ha)';

  const { copied, copy } = useCopyToClipboard(() => {
    const headers = [
      'Crop',
      `Plan ${unitSuffix}`,
      `Actual ${unitSuffix}`,
      `Variance ${unitSuffix}`,
      'Variance (%)',
    ];
    const body = rows.map((row) => {
      const planValue = scaleValue(row.planCost, row.areaHa, metric);
      const actualValue = scaleValue(row.actualCost, row.areaHa, metric);
      const varianceValue =
        planValue !== null && actualValue !== null ? actualValue - planValue : null;
      return [
        row.cropLabel,
        planValue === null ? '' : formatNumber(planValue),
        actualValue === null ? '' : formatNumber(actualValue),
        varianceValue === null ? '' : formatNumber(varianceValue),
        variancePct(row) === null ? '' : formatNumber(variancePct(row)!),
      ];
    });
    body.push([
      'Total',
      formatNumber(totals.plan),
      formatNumber(totals.actual),
      formatNumber(totalVariance),
      totalVariancePct === null ? '' : formatNumber(totalVariancePct),
    ]);
    return buildTsv(headers, body);
  });

  if (!hasAnyData) return null;

  return (
    <SectionCard
      id={SUMMARY_PLAN_VS_ACTUAL_SECTION_ID}
      title="Plan vs Actual Spend"
      subtitle={SUMMARY_DATA_BASIS_PLAN_VS_ACTUAL_NOTE}
      actions={
        <>
          {showCropSelector && (
            <Select
              className="w-48"
              value={activeGroup?.key}
              onChange={setSelectedCropKey}
              options={cropGroups.map((g) => ({ label: g.label, value: g.key }))}
            />
          )}
          <Select<PlanVsActualCategoryFilter>
            className="w-44"
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[
              { label: 'All Categories', value: 'all' },
              ...CATEGORY_FILTER_ORDER.map((category) => ({
                label: SUMMARY_ENTITY_LABELS[category],
                value: category,
              })),
            ]}
          />
          <Segmented<SpendMetric>
            value={metric}
            onChange={setMetric}
            options={[
              { label: 'Total', value: 'total' },
              { label: '$/ha', value: 'perHa' },
            ]}
          />
          {rows.length > 0 && <CopyButton copied={copied} onCopy={copy} />}
        </>
      }
    >
      {rows.length === 0 ? (
        <span className="text-base text-gray-muted">{emptyMessage}</span>
      ) : (
        <>
          <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
            <table className="w-max border-collapse text-base">
              <thead>
                <tr>
                  <th className="max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal">
                    Crop
                  </th>
                  <th className="w-28 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    Plan {unitSuffix}
                  </th>
                  <th className="w-28 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    Actual {unitSuffix}
                  </th>
                  <th className="w-28 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    Variance {unitSuffix}
                  </th>
                  <th className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal">
                    Variance (%)
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const planValue = scaleValue(row.planCost, row.areaHa, metric);
                  const actualValue = scaleValue(row.actualCost, row.areaHa, metric);
                  const varianceValue =
                    planValue !== null && actualValue !== null
                      ? actualValue - planValue
                      : null;
                  const varianceColorClass =
                    varianceValue === null
                      ? ''
                      : varianceValue > 0
                        ? 'text-danger'
                        : varianceValue < 0
                          ? 'text-success'
                          : 'text-charcoal';
                  return (
                    <tr
                      key={row.key}
                      className={`hover:bg-rowHover ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                    >
                      <td className="max-w-[12rem] py-1.5 pl-2 pr-6 text-base text-charcoal">
                        {row.cropLabel}
                      </td>
                      <td className="w-28 px-2 py-1.5 text-right tabular-nums text-charcoal">
                        {planValue === null ? <NoAreaCell /> : formatCurrency(planValue)}
                      </td>
                      <td className="w-28 px-2 py-1.5 text-right tabular-nums text-charcoal">
                        {actualValue === null ? (
                          <NoAreaCell />
                        ) : (
                          formatCurrency(actualValue)
                        )}
                      </td>
                      <td className={`w-28 px-2 py-1.5 text-right tabular-nums ${varianceColorClass}`}>
                        {varianceValue === null ? (
                          <NoAreaCell />
                        ) : (
                          formatCurrency(varianceValue)
                        )}
                      </td>
                      <td className="w-24 px-2 py-1.5 text-right tabular-nums">
                        <VariancePctCell row={row} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="py-1.5 pl-2 pr-6 text-right text-base font-bold text-charcoal">
                    Total:
                  </td>
                  <td className="w-28 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                    {formatCurrency(totals.plan)}
                  </td>
                  <td className="w-28 px-2 py-1.5 text-right font-bold tabular-nums text-charcoal">
                    {formatCurrency(totals.actual)}
                  </td>
                  <td
                    className={`w-28 px-2 py-1.5 text-right font-bold tabular-nums ${totalVariance > 0 ? 'text-danger' : totalVariance < 0 ? 'text-success' : 'text-charcoal'}`}
                  >
                    {formatCurrency(totalVariance)}
                  </td>
                  <td
                    className={`w-24 px-2 py-1.5 text-right font-bold tabular-nums ${(totalVariancePct ?? 0) > 0 ? 'text-danger' : (totalVariancePct ?? 0) < 0 ? 'text-success' : 'text-charcoal'}`}
                  >
                    {totalVariancePct === null ? (
                      <Tooltip title="No plan cost recorded — nothing to compare Actual spend against">
                        <span className="cursor-help font-normal text-gray-muted">
                          —
                        </span>
                      </Tooltip>
                    ) : (
                      `${totalVariancePct > 0 ? '+' : ''}${formatNumber(totalVariancePct)}%`
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-6 w-full rounded-md border border-gray-bright p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-chart-7" />
                Plan
              </span>
              <span className="inline-flex items-center gap-1.5 text-sm text-gray-dark">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-chart-8" />
                Actual
              </span>
            </div>
            <div className="mt-3 flex flex-col gap-3">
              {rows.map((row) => (
                <CropPlanActualBars key={row.key} row={row} max={max} metric={metric} />
              ))}
            </div>
          </div>
        </>
      )}

      {showTimeline && (
        <SpendTimelineChart
          inputs={activeInputs}
          gUnits={gUnits}
          categoryFilter={categoryFilter}
          cropLabel={activeGroup?.label ?? 'this crop'}
          metric={metric}
        />
      )}
    </SectionCard>
  );
};

export default PlanVsActualChart;
